/**
 * Custom date / datetime / time picker — button trigger + non-modal popover.
 *
 * Why this exists: native `<input type="date">` shows the chosen value in the
 * OS-locale format (Safari mm/dd/yyyy, Chrome dd. mm. yyyy, Firefox yyyy-mm-dd).
 * For a CMS where the locale's date format is configured server-side, we need
 * a single chosen format applied identically across browsers. The only way to
 * achieve that is to render the value ourselves.
 *
 * The trigger is a `<button type="button">` (not an `<input type="date">`) so:
 *   - The native picker UI never shows up.
 *   - Clicks inside a parent `<form>` don't accidentally submit.
 *   - The visible label `<label for="filter-foo">` associates with the button
 *     by id (no aria-label needed when a visible label is present).
 *
 * Modes:
 *   - `'date'` (default): calendar grid only. Wire format `'yyyy-MM-dd'`.
 *   - `'datetime'`: calendar grid + `<input type="time">` row. Wire format
 *     `'yyyy-MM-dd HH:mm:ss'`. Day-click does NOT close the popover; focus
 *     moves to the time input.
 *   - `'time'`: time row only (no grid, no Today). Wire format `'HH:mm:ss'`.
 *
 * A11y model: lightweight popup, NOT a modal dialog (no focus trap inside).
 *   - `'date'`: trigger `aria-haspopup="grid"` (unchanged), popover
 *     `role="group"`. Tab past the calendar closes the popover via the
 *     existing focusout-close handler.
 *   - `'datetime'`/`'time'`: trigger `aria-haspopup="dialog"`, popover
 *     `role="dialog"` + `aria-modal="false"`. Calendar inside (datetime mode)
 *     keeps `role="grid"` so the grid semantics aren't lost.
 *
 * Wire-format / display-format separation: `setValue()` / `getValue()` /
 * `onChange` only emit canonical wire strings. Locale config drives display.
 * `setValue()` is ALWAYS silent (never fires `onChange`). Drivers' `clearValue()`
 * delegates here. User-initiated picker actions (day click, time change, Today,
 * Clear) are the only `onChange` emitters.
 *
 * Seconds preservation (datetime + time): when an initial value carries
 * non-zero seconds, the visible time input shows HH:mm only but the wire
 * string round-trips with the original `:ss`. If the user touches the time
 * control, seconds zero-fill on the next emit (and on display when the
 * effective `timeFormat` includes `:ss`).
 */

import { createElement } from '../../core/dom/helpers.js';
import {
  formatDate,
  formatTime,
  toIsoDate,
  normalizeDateWire,
  normalizeTimeWire,
  normalizeDateTimeWire,
  splitDateTimeWire,
} from '../../core/locale/format-date.js';
import { formatFullDate, getMonthGrid, getMonthName, getWeekdayHeaders } from '../../core/locale/calendar-grid.js';

let pickerInstanceCounter = 0;

/**
 * @typedef {'date' | 'datetime' | 'time'} DatePickerMode
 */

/**
 * @typedef {Object} DatePickerOptions
 * @property {string} id - DOM id for the trigger button. Wires to existing `<label for=...>`.
 * @property {string} [value] - Initial wire string per mode. See module header for accepted shapes.
 * @property {string} [placeholder] - Shown on the trigger when value is empty.
 * @property {import('../../contracts/locale.js').LocaleConfig} localeConfig - Required.
 * @property {(wireValue: string) => void} onChange - Called with canonical wire string or '' (Clear). User-initiated only.
 * @property {string} [ariaLabel] - Accessible name when no associated `<label for>` exists. Daterange uses this.
 * @property {DatePickerMode} [mode] - Default `'date'`.
 */

/**
 * @typedef {Object} DatePickerHandle
 * @property {(value: string) => void} setValue - Set wire value (or '' to clear). SILENT.
 * @property {() => string} getValue - Read canonical wire string per mode.
 * @property {(disabled: boolean) => void} setDisabled - Toggle trigger disabled state. Closes popover when disabled while open.
 * @property {(invalid: boolean) => void} setAriaInvalid - Sets aria-invalid on trigger.
 * @property {() => void} destroy - Tear down DOM + listeners.
 */

/**
 * Parse a date-only ISO string `'yyyy-MM-dd'` to a local Date or null.
 * Mirrors format-date.js's strict round-trip parsing (round-trip-validated;
 * year-trap fix).
 *
 * @param {string} iso
 * @returns {Date | null}
 */
function parseIso(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const Y = Number(iso.slice(0, 4));
  const M = Number(iso.slice(5, 7));
  const D = Number(iso.slice(8, 10));
  const d = new Date(Y, M - 1, D);
  d.setFullYear(Y);
  if (d.getFullYear() !== Y || d.getMonth() !== M - 1 || d.getDate() !== D) {
    return null;
  }
  return d;
}

/**
 * True if `a` and `b` represent the same calendar day (local time).
 *
 * @param {Date} a
 * @param {Date} b
 */
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * Local "now" time as `'HH:mm'`.
 * @returns {string}
 */
function nowHHmm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Mount a date / datetime / time picker into `container`.
 *
 * @param {HTMLElement} container
 * @param {DatePickerOptions} opts
 * @returns {DatePickerHandle}
 */
export function createDatePicker(container, opts) {
  const { id, placeholder, localeConfig, onChange, ariaLabel } = opts;
  const mode = /** @type {DatePickerMode} */ (opts.mode ?? 'date');

  pickerInstanceCounter += 1;
  const gridId = `${id}-grid-${pickerInstanceCounter}`;
  const headerId = `${id}-header-${pickerInstanceCounter}`;
  const popoverId = `${id}-popover-${pickerInstanceCounter}`;

  // ---------- State ----------
  // Mode-aware. Date mode: only `currentDate` matters. Datetime mode: all
  // four. Time mode: only `currentTime` / `pristineSeconds` / `timeTouched`.
  /** @type {string} */ let currentDate = '';
  /** @type {string} */ let currentTime = ''; // HH:mm shape (UI value)
  /** @type {string | null} */ let pristineSeconds = null;
  /** @type {boolean} */ let timeTouched = false;

  // Apply initial value via the same path setValue uses (silent).
  applyValueInternal(opts.value);

  // Visible month/year (1..12). Anchored on currentDate if present, else today.
  const initialAnchor = currentDate ? parseIso(currentDate) : null;
  const todayDate = new Date();
  let viewYear = (initialAnchor ?? todayDate).getFullYear();
  let viewMonth = (initialAnchor ?? todayDate).getMonth() + 1;

  // ---------- Trigger button ----------
  const isDialogMode = mode === 'datetime' || mode === 'time';
  const trigger = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    id,
    class: 'date-picker__trigger',
    'aria-haspopup': isDialogMode ? 'dialog' : 'grid',
    'aria-expanded': 'false',
    'aria-controls': isDialogMode ? popoverId : gridId,
    'data-mode': mode,
  }));
  if (ariaLabel) trigger.setAttribute('aria-label', ariaLabel);
  if (localeConfig.rtl) trigger.setAttribute('dir', 'rtl');
  container.appendChild(trigger);

  /**
   * Compute the time-field value to display, applying the same seconds-resolution
   * logic as commit so when `timeFormat` includes `:ss`, preserved seconds appear
   * in the trigger label rather than always `:00`. When `timeFormat` is `'HH:mm'`,
   * the format walker truncates seconds naturally.
   * @returns {string}
   */
  function displayTimeWire() {
    if (currentTime === '') return '';
    const s = timeTouched ? '00' : (pristineSeconds ?? '00');
    return `${currentTime}:${s}`;
  }

  function renderTriggerLabel() {
    let text = '';
    if (mode === 'date') {
      text = currentDate
        ? formatDate(currentDate, localeConfig.dateFormat)
        : (placeholder ?? '');
    } else if (mode === 'datetime') {
      // Datetime mode: a date is required to render a committable value.
      // Without currentDate we show the placeholder even if the user has
      // typed a time — the time is held internally and will appear in the
      // label as soon as a date is picked. This matches `currentWire()`'s
      // truthful-state behavior (no date → empty wire).
      if (currentDate === '') {
        text = placeholder ?? '';
      } else {
        const dateOut = formatDate(currentDate, localeConfig.dateFormat);
        const timeOut = formatTime(displayTimeWire() || '00:00:00', localeConfig.timeFormat);
        text = dateOut + ' ' + timeOut;
      }
    } else {
      // time mode
      text = currentTime
        ? formatTime(displayTimeWire(), localeConfig.timeFormat)
        : (placeholder ?? '');
    }
    trigger.textContent = text;
    const isEmpty = mode === 'date'
      ? currentDate === ''
      : mode === 'datetime'
        ? currentDate === '' // matches currentWire(): no date → no commitable value
        : currentTime === '';
    trigger.classList.toggle('date-picker__trigger--placeholder', isEmpty);
  }
  renderTriggerLabel();

  // ---------- Popover ----------
  /** @type {HTMLDivElement | null} */
  let popover = null;
  /** @type {HTMLDivElement | null} */
  let gridEl = null;
  /** @type {HTMLDivElement | null} */
  let headerEl = null;
  /** @type {HTMLButtonElement | null} */
  let prevBtn = null;
  /** @type {HTMLButtonElement | null} */
  let nextBtn = null;
  /** @type {HTMLInputElement | null} */
  let timeInput = null;
  let isOpen = false;
  let destroyed = false;

  function position() {
    if (!popover) return;
    const rect = trigger.getBoundingClientRect();
    if (localeConfig.rtl) {
      // Right-align popover right edge to trigger right edge before measuring,
      // so RTL layouts flow visually correctly. Viewport clamp below still applies.
      const popRectInitial = popover.getBoundingClientRect();
      popover.style.left = `${rect.right - popRectInitial.width}px`;
    } else {
      popover.style.left = `${rect.left}px`;
    }
    popover.style.top = `${rect.bottom + 4}px`;
    requestAnimationFrame(() => {
      if (!popover) return;
      const popRect = popover.getBoundingClientRect();
      if (popRect.bottom > window.innerHeight) {
        popover.style.top = `${rect.top - popRect.height - 4}px`;
      }
      if (popRect.right > window.innerWidth) {
        popover.style.left = `${Math.max(8, window.innerWidth - popRect.width - 8)}px`;
      }
      if (popRect.left < 0) {
        popover.style.left = '8px';
      }
    });
  }

  function handleReposition() { if (isOpen) position(); }

  /** @param {MouseEvent} e */
  function handleClickOutside(e) {
    if (!isOpen || !popover) return;
    const target = /** @type {Node | null} */ (e.target);
    if (!target) return;
    if (!popover.contains(target) && !trigger.contains(target)) close();
  }

  /** @param {KeyboardEvent} e */
  function handleEscape(e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  /** @param {FocusEvent} e */
  function handleFocusOut(e) {
    if (!isOpen || !popover) return;
    const next = /** @type {Node | null} */ (e.relatedTarget);
    queueMicrotask(() => {
      if (!isOpen || !popover) return;
      const active = document.activeElement;
      const stillInside = (next && popover.contains(next))
        || (active && popover.contains(active));
      if (!stillInside) close({ restoreFocus: false });
    });
  }

  function open() {
    if (isOpen || destroyed) return;
    isOpen = true;
    trigger.setAttribute('aria-expanded', 'true');
    /** @type {Record<string, string>} */
    const popoverAttrs = {
      class: 'date-picker__popover',
      id: popoverId,
      'data-mode': mode,
    };
    if (isDialogMode) {
      popoverAttrs.role = 'dialog';
      popoverAttrs['aria-modal'] = 'false';
      if (mode === 'datetime') popoverAttrs['aria-labelledby'] = headerId;
    } else {
      popoverAttrs.role = 'group';
      popoverAttrs['aria-labelledby'] = headerId;
    }
    popover = /** @type {HTMLDivElement} */ (createElement('div', popoverAttrs));
    if (localeConfig.rtl) popover.setAttribute('dir', 'rtl');
    popover.style.position = 'fixed';
    document.body.appendChild(popover);
    buildPopoverDom();
    if (mode !== 'time') renderGrid();
    position();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    popover.addEventListener('focusout', handleFocusOut);
    window.addEventListener('scroll', handleReposition, { passive: true, capture: true });
    window.addEventListener('resize', handleReposition, { passive: true });
    queueMicrotask(focusInitialFocusable);
  }

  /** @param {{ restoreFocus?: boolean }} [options] */
  function close(options) {
    const { restoreFocus = true } = options ?? {};
    if (!isOpen) return;
    isOpen = false;
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEscape, true);
    window.removeEventListener('scroll', handleReposition, /** @type {any} */ ({ capture: true }));
    window.removeEventListener('resize', handleReposition);
    if (popover) {
      popover.removeEventListener('focusout', handleFocusOut);
      popover.remove();
      popover = null;
      gridEl = null;
      headerEl = null;
      prevBtn = null;
      nextBtn = null;
      timeInput = null;
    }
    if (!destroyed && restoreFocus) trigger.focus();
  }

  function buildPopoverDom() {
    if (!popover) return;

    if (mode !== 'time') {
      // Header: prev / month-year label / next
      const header = createElement('div', { class: 'date-picker__header' });
      prevBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button',
        class: 'date-picker__nav date-picker__nav--prev',
        'aria-label': 'Previous month',
      }));
      prevBtn.textContent = '‹';
      nextBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button',
        class: 'date-picker__nav date-picker__nav--next',
        'aria-label': 'Next month',
      }));
      nextBtn.textContent = '›';
      headerEl = /** @type {HTMLDivElement} */ (createElement('div', {
        class: 'date-picker__title',
        id: headerId,
        'aria-live': 'polite',
      }));
      header.appendChild(prevBtn);
      header.appendChild(headerEl);
      header.appendChild(nextBtn);
      popover.appendChild(header);

      // Weekday header row.
      const calendarLocale = localeConfig.displayLocale || localeConfig.locale;
      const headerRow = createElement('div', { class: 'date-picker__weekdays' });
      for (const name of getWeekdayHeaders(localeConfig.firstDayOfWeek, calendarLocale)) {
        const cell = createElement('div', { class: 'date-picker__weekday' });
        cell.textContent = name;
        headerRow.appendChild(cell);
      }
      popover.appendChild(headerRow);

      // Grid
      gridEl = /** @type {HTMLDivElement} */ (createElement('div', {
        class: 'date-picker__grid',
        id: gridId,
        role: 'grid',
        'aria-labelledby': headerId,
        'aria-rowcount': '6',
        'aria-colcount': '7',
      }));
      popover.appendChild(gridEl);

      // Nav handlers — same Safari mousedown-preventDefault as day cells.
      prevBtn.addEventListener('mousedown', (e) => e.preventDefault());
      prevBtn.addEventListener('click', () => {
        shiftMonth(-1);
        focusInitialCell();
      });
      nextBtn.addEventListener('mousedown', (e) => e.preventDefault());
      nextBtn.addEventListener('click', () => {
        shiftMonth(1);
        focusInitialCell();
      });

      gridEl.addEventListener('keydown', handleGridKeydown);
    }

    // Time row (datetime + time modes).
    if (mode !== 'date') {
      const row = createElement('div', { class: 'date-picker__time-row' });
      const timeLabelId = `${id}-timelabel-${pickerInstanceCounter}`;
      const labelEl = createElement('label', {
        class: 'date-picker__time-label',
        id: timeLabelId,
      });
      labelEl.textContent = 'Time';
      timeInput = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'time',
        step: '60',
        class: 'date-picker__time-input',
        'aria-labelledby': timeLabelId,
      }));
      labelEl.setAttribute('for', `${id}-timeinput-${pickerInstanceCounter}`);
      timeInput.id = `${id}-timeinput-${pickerInstanceCounter}`;
      // Programmatic value is HH:mm only — never HH:mm:ss with step="60".
      timeInput.value = currentTime;
      timeInput.addEventListener('change', handleTimeInputChange);
      row.appendChild(labelEl);
      row.appendChild(timeInput);
      popover.appendChild(row);
    }

    // Footer: Today / Clear (date + datetime). Time mode: Clear only.
    const footer = createElement('div', { class: 'date-picker__footer' });
    if (mode !== 'time') {
      const todayBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button',
        class: 'date-picker__action',
      }));
      todayBtn.textContent = 'Today';
      todayBtn.addEventListener('mousedown', (e) => e.preventDefault());
      todayBtn.addEventListener('click', handleTodayClick);
      footer.appendChild(todayBtn);
    }
    const clearBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      class: 'date-picker__action date-picker__action--clear',
    }));
    clearBtn.textContent = 'Clear';
    clearBtn.addEventListener('mousedown', (e) => e.preventDefault());
    clearBtn.addEventListener('click', handleClearClick);
    footer.appendChild(clearBtn);
    popover.appendChild(footer);
  }

  /** @param {number} delta */
  function shiftMonth(delta) {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    viewMonth = m;
    viewYear = y;
    renderGrid();
  }

  function renderGrid() {
    if (!popover || !gridEl || !headerEl) return;
    const calendarLocale = localeConfig.displayLocale || localeConfig.locale;
    headerEl.textContent = getMonthName(new Date(viewYear, viewMonth - 1, 1), calendarLocale);
    gridEl.innerHTML = '';
    const grid = getMonthGrid(viewYear, viewMonth, localeConfig.firstDayOfWeek);
    const selected = currentDate ? parseIso(currentDate) : null;
    const today = new Date();
    let rowIdx = 0;
    for (const week of grid) {
      const rowEl = createElement('div', { class: 'date-picker__row', role: 'row' });
      let colIdx = 0;
      for (const cell of week) {
        const isSelected = selected !== null && sameDay(cell.date, selected);
        const isToday = sameDay(cell.date, today);
        const cellEl = /** @type {HTMLButtonElement} */ (createElement('button', {
          type: 'button',
          role: 'gridcell',
          class: 'date-picker__cell'
            + (cell.otherMonth ? ' date-picker__cell--other-month' : '')
            + (isToday ? ' date-picker__cell--today' : '')
            + (isSelected ? ' date-picker__cell--selected' : ''),
          tabindex: '-1',
          'data-iso': toIsoDate(cell.date),
          'data-row': String(rowIdx),
          'data-col': String(colIdx),
        }));
        cellEl.textContent = String(cell.date.getDate());
        const baseLabel = formatFullDate(cell.date, calendarLocale);
        cellEl.setAttribute(
          'aria-label',
          cell.otherMonth ? `${baseLabel} (outside current month)` : baseLabel,
        );
        if (isSelected) cellEl.setAttribute('aria-selected', 'true');
        if (isToday) cellEl.setAttribute('aria-current', 'date');
        cellEl.addEventListener('mousedown', (e) => e.preventDefault());
        cellEl.addEventListener('click', () => selectDate(cell.date));
        rowEl.appendChild(cellEl);
        colIdx += 1;
      }
      gridEl.appendChild(rowEl);
      rowIdx += 1;
    }
  }

  /**
   * Day-cell click handler. Mode-aware:
   * - `date`: commit + emit + close.
   * - `datetime`: commit combined wire string, emit, do NOT close, focus time input.
   *
   * @param {Date} date
   */
  function selectDate(date) {
    const iso = toIsoDate(date);
    if (!iso) return;
    currentDate = iso;
    viewYear = date.getFullYear();
    viewMonth = date.getMonth() + 1;
    if (mode === 'date') {
      renderTriggerLabel();
      onChange(iso);
      close();
      return;
    }
    // datetime mode: keep popover open, focus time input next.
    // Default empty time to '00:00' BEFORE rendering — otherwise the trigger
    // label briefly shows date with trailing space ("01.01.2024 ") because
    // formatTime('', tf) returns ''. Set state first, then render.
    if (currentTime === '') currentTime = '00:00';
    if (timeInput && timeInput.value !== currentTime) timeInput.value = currentTime;
    renderTriggerLabel();
    onChange(currentWire());
    queueMicrotask(() => {
      if (destroyed || !isOpen || !timeInput) return;
      timeInput.focus();
    });
  }

  function handleTimeInputChange() {
    if (!timeInput) return;
    const raw = timeInput.value; // HH:mm or '' from native input
    if (raw === '') {
      // User cleared the time input.
      if (mode === 'time') {
        currentTime = '';
        pristineSeconds = null;
        timeTouched = true;
        renderTriggerLabel();
        onChange('');
        return;
      }
      // datetime: with currentDate set, treat as touched-set-to-midnight.
      if (currentDate !== '') {
        currentTime = '00:00';
        pristineSeconds = null;
        timeTouched = true;
        if (timeInput.value !== '00:00') timeInput.value = '00:00';
        renderTriggerLabel();
        onChange(`${currentDate} 00:00:00`);
      } else {
        // datetime, no date picked yet — clearing the (already-empty-state)
        // time input means "still nothing committed". Don't fabricate a date.
        currentTime = '';
        pristineSeconds = null;
        timeTouched = false;
        renderTriggerLabel();
      }
      return;
    }
    // Non-empty value: HH:mm shape per native input. Touch.
    currentTime = raw;
    timeTouched = true;
    pristineSeconds = null;
    renderTriggerLabel();
    if (mode === 'datetime' && currentDate === '') {
      // Datetime mode without a date: the user's typed time is stored locally
      // and will roll into the wire string the moment they pick a date. Do
      // NOT fabricate today's date in onChange — that would silently emit a
      // datetime the user never committed. The picker's currentWire() also avoids fabrication for
      // the same reason; getValue() returns ''.
      return;
    }
    onChange(currentWire());
  }

  function handleTodayClick() {
    const now = new Date();
    if (mode === 'date') {
      selectDate(now);
      return;
    }
    // datetime: today's date, preserve currentTime if present, otherwise local now.
    const iso = toIsoDate(now);
    currentDate = iso;
    viewYear = now.getFullYear();
    viewMonth = now.getMonth() + 1;
    if (currentTime === '') {
      currentTime = nowHHmm();
      pristineSeconds = null;
      timeTouched = false;
    }
    if (timeInput && timeInput.value !== currentTime) timeInput.value = currentTime;
    if (mode !== 'time' && gridEl) renderGrid();
    renderTriggerLabel();
    onChange(currentWire());
    queueMicrotask(() => {
      if (destroyed || !isOpen || !timeInput) return;
      timeInput.focus();
    });
  }

  function handleClearClick() {
    currentDate = '';
    currentTime = '';
    pristineSeconds = null;
    timeTouched = false;
    if (timeInput) timeInput.value = '';
    renderTriggerLabel();
    if (mode !== 'time' && gridEl) renderGrid();
    onChange('');
    close();
  }

  /**
   * Compose the canonical wire string for the current state per mode.
   *
   * Datetime mode requires a date. Without `currentDate`, the picker has no
   * commitable datetime — `currentWire()` returns `''` rather than fabricating
   * today's date (would violate §3 truthful-state invariant). The user's
   * typed time is preserved in `currentTime` and rolls into the wire the
   * moment they pick a date via `selectDate` / `handleTodayClick`.
   *
   * @returns {string}
   */
  function currentWire() {
    if (mode === 'date') return currentDate;
    if (mode === 'datetime') {
      if (currentDate === '') return '';
      const time = currentTime || '00:00';
      const seconds = timeTouched ? '00' : (pristineSeconds ?? '00');
      return `${currentDate} ${time}:${seconds}`;
    }
    // time mode
    if (currentTime === '') return '';
    const seconds = timeTouched ? '00' : (pristineSeconds ?? '00');
    return `${currentTime}:${seconds}`;
  }

  function focusInitialFocusable() {
    if (!isOpen || destroyed) return;
    if (mode === 'time') {
      if (timeInput) timeInput.focus();
      return;
    }
    focusInitialCell();
  }

  function focusInitialCell() {
    if (!gridEl) return;
    const selectedIso = currentDate;
    /** @type {HTMLButtonElement | null} */
    let target = null;
    if (selectedIso) {
      target = /** @type {HTMLButtonElement | null} */ (gridEl.querySelector(
        `.date-picker__cell:not(.date-picker__cell--other-month)[data-iso="${selectedIso}"]`,
      ));
    }
    if (!target) {
      const todayIsoStr = toIsoDate(new Date());
      target = /** @type {HTMLButtonElement | null} */ (gridEl.querySelector(
        `.date-picker__cell:not(.date-picker__cell--other-month)[data-iso="${todayIsoStr}"]`,
      ));
    }
    if (!target) {
      target = /** @type {HTMLButtonElement | null} */ (gridEl.querySelector(
        '.date-picker__cell:not(.date-picker__cell--other-month)',
      ));
    }
    if (target) {
      const cells = gridEl.querySelectorAll('.date-picker__cell');
      cells.forEach((c) => c.setAttribute('tabindex', '-1'));
      target.setAttribute('tabindex', '0');
      target.focus();
    }
  }

  /** @param {KeyboardEvent} e */
  function handleGridKeydown(e) {
    if (!gridEl) return;
    const active = /** @type {HTMLButtonElement | null} */ (document.activeElement);
    if (!active || !gridEl.contains(active)) return;
    const iso = active.getAttribute('data-iso');
    if (!iso) return;
    const date = parseIso(iso);
    if (!date) return;
    const safeDate = date;

    /** @param {number} dayDelta */
    function move(dayDelta) {
      const next = new Date(safeDate.getFullYear(), safeDate.getMonth(), safeDate.getDate() + dayDelta);
      moveTo(next);
    }
    /** @param {number} monthDelta */
    function moveByMonth(monthDelta) {
      const next = new Date(safeDate.getFullYear(), safeDate.getMonth() + monthDelta, safeDate.getDate());
      moveTo(next);
    }
    /** @param {Date} target */
    function moveTo(target) {
      if (target.getFullYear() !== viewYear || target.getMonth() + 1 !== viewMonth) {
        viewYear = target.getFullYear();
        viewMonth = target.getMonth() + 1;
        renderGrid();
      }
      const targetIso = toIsoDate(target);
      if (!gridEl) return;
      const cell = /** @type {HTMLButtonElement | null} */ (gridEl.querySelector(
        `.date-picker__cell:not(.date-picker__cell--other-month)[data-iso="${targetIso}"]`,
      ));
      if (cell) {
        gridEl.querySelectorAll('.date-picker__cell').forEach((c) => c.setAttribute('tabindex', '-1'));
        cell.setAttribute('tabindex', '0');
        cell.focus();
      }
    }

    switch (e.key) {
      case 'ArrowLeft':  e.preventDefault(); move(-1); break;
      case 'ArrowRight': e.preventDefault(); move(1); break;
      case 'ArrowUp':    e.preventDefault(); move(-7); break;
      case 'ArrowDown':  e.preventDefault(); move(7); break;
      case 'PageUp':     e.preventDefault(); moveByMonth(-1); break;
      case 'PageDown':   e.preventDefault(); moveByMonth(1); break;
      case 'Home':       e.preventDefault(); moveTo(new Date(date.getFullYear(), date.getMonth(), 1)); break;
      case 'End': {
        e.preventDefault();
        const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        moveTo(last);
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectDate(date);
        break;
      default:
        break;
    }
  }

  // ---------- Trigger interaction ----------
  trigger.addEventListener('mousedown', (e) => {
    if (isOpen) e.preventDefault();
  });
  trigger.addEventListener('click', () => {
    if (trigger.disabled) return;
    if (isOpen) close(); else open();
  });
  /** @param {KeyboardEvent} e */
  trigger.addEventListener('keydown', (e) => {
    if (trigger.disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) open();
    }
  });

  /**
   * Apply a wire-string value to internal state without touching the DOM
   * label / popover / `onChange`. Used by the constructor and by the public
   * `setValue` (which then refreshes UI).
   *
   * @param {unknown} value
   */
  function applyValueInternal(value) {
    if (mode === 'date') {
      currentDate = normalizeDateWire(value);
      currentTime = '';
      pristineSeconds = null;
      timeTouched = false;
      return;
    }
    if (mode === 'time') {
      const wire = normalizeTimeWire(value); // 'HH:mm:ss' or ''
      if (wire === '') {
        currentTime = '';
        pristineSeconds = null;
      } else {
        currentTime = wire.slice(0, 5);
        const ss = wire.slice(6, 8);
        pristineSeconds = ss === '00' ? null : ss;
      }
      currentDate = '';
      timeTouched = false;
      return;
    }
    // datetime
    const wire = normalizeDateTimeWire(value); // 'yyyy-MM-dd HH:mm:ss' or ''
    if (wire === '') {
      currentDate = '';
      currentTime = '';
      pristineSeconds = null;
    } else {
      const split = splitDateTimeWire(wire);
      // Defensive — splitDateTimeWire returns null only for invalid input,
      // but normalizeDateTimeWire guarantees validity above.
      const dateStr = split ? split.date : wire.slice(0, 10);
      const timeStr = split ? split.time : wire.slice(11);
      currentDate = dateStr;
      currentTime = timeStr.slice(0, 5);
      const ss = timeStr.slice(6, 8);
      pristineSeconds = ss === '00' ? null : ss;
    }
    timeTouched = false;
  }

  return {
    setValue(v) {
      applyValueInternal(v);
      // Anchor view to new date when present.
      if (mode !== 'time' && currentDate) {
        const parsed = parseIso(currentDate);
        if (parsed) {
          viewYear = parsed.getFullYear();
          viewMonth = parsed.getMonth() + 1;
        }
      }
      renderTriggerLabel();
      if (isOpen) {
        if (mode !== 'time' && gridEl) renderGrid();
        if (timeInput) {
          // Programmatic assignment — does NOT fire `change` event in jsdom or browsers.
          if (timeInput.value !== currentTime) timeInput.value = currentTime;
        }
      }
    },
    getValue() {
      return currentWire();
    },
    setDisabled(disabled) {
      trigger.disabled = !!disabled;
      if (disabled && isOpen) close();
    },
    setAriaInvalid(invalid) {
      trigger.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
    destroy() {
      destroyed = true;
      close();
      trigger.remove();
    },
  };
}
